import {
  type AccountInfo as MsalAccountInfo,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  type IPublicClientApplication as MsalClientApplication,
} from '@azure/msal-browser'
import {
  getObjectEntries,
  listUtils,
  type OpenAPIConfig,
  type Optional,
} from '@festina-life/core'

import { getAuthenticationActions } from '../hooks/useMsal/getAuthenticationActions'
import { OpenAPI as MsGraphOpenapi } from '../msGraph/openapi'
import type { EnvironmentSettings } from '../settings/fetchAppSettings'
import type { AllFestinaComponents } from './types'

type OpenApiDescriptors = Record<string, Optional<OpenAPIConfig[]>>

export type AcquireTokenProps = {
  accountInfo: MsalAccountInfo
  clientApplication: MsalClientApplication
  audience: string
  redirectUri?: string
}

export const getToken =
  ({
    accountInfo,
    clientApplication,
    audience,
    redirectUri,
  }: AcquireTokenProps) =>
  async (): Promise<string> => {
    if (clientApplication.getAllAccounts().length === 0) {
      return ''
    }

    const request = {
      scopes: [audience],
      account: accountInfo,
      redirectUri: `${redirectUri ?? window.location.origin}/blank.html`,
    }

    const { acquireTokenInteractively } = getAuthenticationActions({
      instance: clientApplication,
      redirectUri,
    })

    try {
      /**
       * This is a workaround to get rid of console errors about:
       * "Cannot update a component (`MsalProvider`) while rendering a different component
       * Taken from here https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/5796#issuecomment-1763461620
       */
      await Promise.resolve() //< This call make sure we're now running in a different MicroTask

      const authenticationResult =
        await clientApplication.acquireTokenSilent(request)

      return authenticationResult.accessToken
    } catch (e) {
      const isInteractionRequired =
        e instanceof InteractionRequiredAuthError ||
        (e instanceof BrowserAuthError &&
          e.errorCode === BrowserAuthErrorCodes.timedOut)

      if (isInteractionRequired) {
        await acquireTokenInteractively({
          scopes: [audience],
        })

        return ''
      } else {
        throw e
      }
    }
  }

const knownFestinaComponents: string[] = listUtils.arrayOfAllUnique<
  AllFestinaComponents[number]
>()([
  'life',
  'lifeDataReplicator',
  'lifeDataReplicatorTest',
  'lifeTest',
  'unitLink',
  'unitLinkTest',
  'unitLinkDataReplicator',
  'unitLinkDataReplicatorTest',
  'legalEntity',
  'legalEntityTest',
  'legalEntityDataReplicator',
  'legalEntityDataReplicatorTest',
  'moneyIn',
  'moneyInTest',
  'moneyOut',
  'moneyOutTest',
  'dataMart',
  'dataMartTest',
  'dataMart2',
  'dataMart2Test',
  'configuration',
  'configurationTest',
  'collCalc',
  'collCalcTest',
  'resetTest',
])

export function setupServices({
  clientApplication,
  services,
  environmentSettings,
  accountInfo,
}: {
  clientApplication: MsalClientApplication
  services: OpenApiDescriptors
  environmentSettings: EnvironmentSettings
  accountInfo: MsalAccountInfo
}) {
  getObjectEntries(services).forEach(([serviceKey, configs]) =>
    configs?.forEach((OpenApiInstance: OpenAPIConfig) => {
      const prefix = 'REACT_APP_'
      const namingConvention = knownFestinaComponents
        .map((value) => value.toLocaleLowerCase())
        .includes(serviceKey.toLocaleLowerCase())
        ? serviceKey.toUpperCase()
        : `EXTRA_${serviceKey.toUpperCase()}`
      const namingConventionAPI =
        `${prefix + namingConvention}_API_ENDPOINT` as keyof typeof environmentSettings
      const namingConventionAudience =
        `${prefix + namingConvention}_AUDIENCE` as keyof typeof environmentSettings

      // This should be built into our type generators
      OpenApiInstance.ENCODE_PATH = encodeURIComponent

      if (typeof environmentSettings[namingConventionAPI] === 'string') {
        OpenApiInstance.BASE = environmentSettings[namingConventionAPI]
      }

      if (typeof environmentSettings[namingConventionAudience] === 'string') {
        OpenApiInstance.TOKEN = getToken({
          accountInfo,
          clientApplication,
          audience: environmentSettings[namingConventionAudience],
          redirectUri: environmentSettings.REACT_APP_REDIRECT_URI,
        })
      }
    }),
  )

  if (environmentSettings.REACT_APP_SHOWUSERINFO) {
    MsGraphOpenapi.TOKEN = getToken({
      accountInfo,
      clientApplication,
      audience: 'User.Read',
      redirectUri: environmentSettings.REACT_APP_REDIRECT_URI,
    })

    const { REACT_APP_MSGRAPH_API_ENDPOINT } = environmentSettings

    if (REACT_APP_MSGRAPH_API_ENDPOINT) {
      MsGraphOpenapi.BASE = REACT_APP_MSGRAPH_API_ENDPOINT
    }
  }
}
